import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useToast } from '@/hooks/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useTranslation } from 'react-i18next';
import type { SignUpFormData, LoginFormData } from '@/lib/authSchemas';
import {
  buildCompanyInviteLink,
  claimInvitationCode,
  clearPendingInvitationClaim,
  savePendingInvitationClaim,
} from '@/lib/company-invite-utils';
import { createLogger } from '@/lib/structuredLogger';

const logger = createLogger('useAuthForm');

export const useAuthForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleLogin = async (formData: LoginFormData) => {
    setIsLoading(true);
    setErrors({});

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
      });

      if (error) throw error;

      if (data.user) {
        return;
      }
    } catch (error: unknown) {
      logger.error('Login failed', error instanceof Error ? error : new Error(String(error)), {
        email: formData.email, // Will be auto-redacted
      });
      const errorMessage = error instanceof Error ? error.message : t('auth.invalidCredentials');
      toast({
        title: t('auth.loginError'),
        description: errorMessage,
        variant: 'destructive',
      });
      setErrors({ submit: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUp = async (
    formData: SignUpFormData & { companyId?: string; invitationCode?: string },
  ) => {
    setIsLoading(true);
    setErrors({});

    try {
      if (formData.invitationCode) {
        savePendingInvitationClaim(formData.invitationCode);
      }

      const { data, error } = await supabase.auth.signUp({
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        options: {
          emailRedirectTo: formData.invitationCode
            ? buildCompanyInviteLink(window.location.origin, formData.invitationCode)
            : undefined,
          data: {
            company_id: formData.companyId,
            invitation_code: formData.invitationCode?.toUpperCase().trim(),
            consentDataProcessing: formData.consentDataProcessing,
            consentAiTips: formData.consentDataProcessing && formData.consentAiTips,
            consentAnonymousInsights:
              formData.consentDataProcessing && formData.consentAnonymousInsights,
            privacyPolicyAccepted: formData.privacyPolicyAccepted,
            termsAccepted: formData.termsAccepted,
            // Note: department, gender, and age are collected during onboarding (Anamnesebogen)
          },
        },
      });

      if (error) throw error;

      if (data.session && formData.invitationCode) {
        const { result, error: claimError } = await claimInvitationCode(
          supabase,
          formData.invitationCode,
        );

        if (claimError || !result?.success) {
          await supabase.auth.signOut();
          throw new Error(
            result?.error_message || claimError?.message || 'Failed to complete company invitation',
          );
        }

        clearPendingInvitationClaim();
      }

      if (data.session) {
        toast({
          title: t('auth.signupSuccess'),
        });
        navigate('/health-consent');
        return true;
      } else if (data.user) {
        toast({
          title: t('auth.signupSuccess'),
          description: t('auth.checkEmail'),
        });
        return true;
      }
    } catch (error: unknown) {
      logger.error('Signup failed', error instanceof Error ? error : new Error(String(error)), {
        email: formData.email, // Will be auto-redacted
        department: formData.department,
      });
      const errorMessage = error instanceof Error ? error.message : t('auth.signupError');
      toast({
        title: t('auth.signupError'),
        description: errorMessage,
        variant: 'destructive',
      });
      setErrors({ submit: errorMessage });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (email: string) => {
    setIsLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      toast({
        title: t('auth.resetLinkSent'),
        description: t('auth.checkEmailForReset'),
      });
      return true;
    } catch (error: unknown) {
      logger.error(
        'Password reset failed',
        error instanceof Error ? error : new Error(String(error)),
        {
          email, // Will be auto-redacted
        },
      );
      const errorMessage = error instanceof Error ? error.message : t('auth.resetError');
      toast({
        title: t('auth.resetError'),
        description: errorMessage,
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    errors,
    setErrors,
    showPassword,
    setShowPassword,
    showConfirmPassword,
    setShowConfirmPassword,
    handleLogin,
    handleSignUp,
    handlePasswordReset,
  };
};
